import { useMemo } from 'react';

import { advertiserTypes, furnishedUnfurnished, housingTypes, oldOrNewTypes } from 'src/constants/searchAdsTypes';
import useUser from 'src/hooks/useUser';
import { FurnishedStatus } from 'src/types/__generated__/graphql';

export type PropertyTypeGroup = 'furnished' | 'housing' | 'advertiser' | 'oldOrNew';

type AdvertiserTypeWithInterOffice = (typeof advertiserTypes)[number] | 'interOffice';
type Type =
    | (typeof housingTypes)[number]
    | AdvertiserTypeWithInterOffice
    | (typeof oldOrNewTypes)[number]
    | (typeof furnishedUnfurnished)[number];

export type Item = {
    type: 'Item';
    value: Type;
    label: string;
    isChecked: boolean;
    toggle: () => void;
};

export type GroupItem = {
    type: 'GroupItem';
    label: string;
};

export type FormValues = Record<Exclude<Type, 'furnished' | 'unfurnished'>, boolean | undefined> & {
    furnished: FurnishedStatus[] | undefined;
};

type Params = {
    formValues: FormValues;
    setFieldValue: <K extends keyof FormValues, V extends FormValues[K]>(field: K, value: V) => void;
    onItemValueChange?: (value: Partial<FormValues>) => void;
    groupTypesToDisplay: PropertyTypeGroup[];
};

export const usePropertyTypesItems = ({
    formValues,
    setFieldValue,
    onItemValueChange,
    groupTypesToDisplay,
}: Params) => {
    const currentUser = useUser();

    const items = useMemo(() => {
        let _items: Array<Item | GroupItem> = [];

        const advertiserTypesObjects: Item[] = [
            ...advertiserTypes,
            ...(currentUser?.isRealEstatesTrades ? ['interOffice' as AdvertiserTypeWithInterOffice] : []),
        ].map((_type) => ({
            type: 'Item',
            label: _type,
            value: _type,
            isChecked: formValues[_type] ?? false,
            toggle() {
                const newValue = !formValues[_type];

                setFieldValue(_type, newValue);
                onItemValueChange?.({ [_type]: newValue });
            },
        }));

        const oldOrNewTypesObjects: Item[] = oldOrNewTypes.map((_type) => ({
            type: 'Item',
            label: _type,
            value: _type,
            isChecked: formValues[_type] ?? false,
            toggle() {
                const newValue = !formValues[_type];

                setFieldValue(_type, newValue);
                onItemValueChange?.({ [_type]: newValue });
            },
        }));

        if (groupTypesToDisplay.includes('furnished')) {
            const furnishedTypesObjects: Item[] = furnishedUnfurnished.map((_type) => ({
                type: 'Item',
                label: _type,
                value: _type,
                isChecked:
                    formValues.furnished?.includes(
                        _type === 'furnished' ? FurnishedStatus.Furnished : FurnishedStatus.Unfurnished,
                    ) ?? false,
                toggle() {
                    const actualFurnishedValues = formValues.furnished ?? [];

                    const isSelected = actualFurnishedValues.includes(
                        _type === 'furnished' ? FurnishedStatus.Furnished : FurnishedStatus.Unfurnished,
                    );
                    const newValue = !isSelected;

                    let newFieldValue: FurnishedStatus[];
                    if (newValue) {
                        newFieldValue = [
                            ...actualFurnishedValues,
                            _type === 'furnished' ? FurnishedStatus.Furnished : FurnishedStatus.Unfurnished,
                        ];
                    } else {
                        newFieldValue = actualFurnishedValues.filter(
                            (value) =>
                                value !==
                                (_type === 'furnished' ? FurnishedStatus.Furnished : FurnishedStatus.Unfurnished),
                        );
                    }
                    setFieldValue('furnished', newFieldValue);
                    onItemValueChange?.({ furnished: newFieldValue });
                },
            }));
            _items = [
                ..._items,
                {
                    type: 'GroupItem',
                    label: 'furnishedUnfurnished',
                },
                ...furnishedTypesObjects,
            ];
        }
        if (groupTypesToDisplay.includes('housing')) {
            const housingTypesObjects: Item[] = housingTypes.map((_type) => ({
                type: 'Item',
                label: _type,
                value: _type,
                isChecked: formValues[_type] ?? false,
                toggle() {
                    const newValue = !formValues[_type];

                    setFieldValue(_type, newValue);
                    onItemValueChange?.({ [_type]: newValue });
                },
            }));
            _items = [
                ..._items,
                {
                    type: 'GroupItem',
                    label: 'propertyType',
                },
                ...housingTypesObjects,
            ];
        }
        if (groupTypesToDisplay.includes('advertiser')) {
            _items = [
                ..._items,
                {
                    type: 'GroupItem',
                    label: 'advertiserType',
                },
                ...advertiserTypesObjects,
            ];
        }
        if (groupTypesToDisplay.includes('oldOrNew')) {
            _items = [
                ..._items,
                {
                    type: 'GroupItem',
                    label: 'sellType',
                },
                ...oldOrNewTypesObjects,
            ];
        }
        return _items;
    }, [currentUser?.isRealEstatesTrades, formValues, groupTypesToDisplay, onItemValueChange, setFieldValue]);

    return items;
};
